import React, { useState, useEffect, useCallback } from "react";
import { getRuleViolations, getRuleConfig, updateRuleConfig } from "../lib/api";
import { ShieldAlert, ShieldCheck, AlertTriangle, Info, Settings2, Loader2, RefreshCw, Copy, Layers, PieChart, Scale, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const RULE_META = {
  stock_concentration: { label: "Stock Concentration", icon: AlertTriangle, color: "text-amber-400" },
  sector_limits: { label: "Sector Limits", icon: PieChart, color: "text-orange-400" },
  duplicate: { label: "Duplicate Holdings", icon: Copy, color: "text-blue-400" },
  mf_overlap: { label: "MF Overlap", icon: Layers, color: "text-purple-400" },
  rebalancing: { label: "Rebalancing", icon: Scale, color: "text-teal-400" },
};

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", badge: "bg-red-500/20 text-red-400" },
  warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", badge: "bg-amber-500/20 text-amber-400" },
  info: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", badge: "bg-blue-500/20 text-blue-400" },
};

export default function RulesPage() {
  const [violations, setViolations] = useState(null);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [localConfig, setLocalConfig] = useState({});
  const [filter, setFilter] = useState("all");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([getRuleViolations(), getRuleConfig()]);
      setViolations(vRes.data.violations || []);
      setSummary(vRes.data.summary || {});
      setConfig(vRes.data.config || cRes.data);
      setLocalConfig(vRes.data.config || cRes.data);
    } catch (err) {
      console.error("Failed to load rules:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRuleConfig({
        max_stock_pct: localConfig.max_stock_pct,
        max_sector_pct: localConfig.max_sector_pct,
        rules_enabled: localConfig.rules_enabled,
      });
      toast.success("Rules configuration saved");
      setShowConfig(false);
      loadData();
    } catch {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const filtered = violations
    ? filter === "all"
      ? violations
      : violations.filter(v => v.rule === filter)
    : [];

  const ruleCounts = {};
  (violations || []).forEach(v => {
    ruleCounts[v.rule] = (ruleCounts[v.rule] || 0) + 1;
  });

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin text-[var(--brand)]" size={32} /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Rule Engine</h1>
          <p className="text-sm text-[var(--text-muted)]">Portfolio compliance rules, limits & rebalancing suggestions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowConfig(!showConfig)} className="border-white/10 text-[var(--text-secondary)] hover:text-white rounded text-xs" data-testid="config-toggle-btn">
            <Settings2 size={14} className="mr-1" />{showConfig ? "Hide" : "Configure"}
          </Button>
          <Button onClick={loadData} className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded text-xs" data-testid="refresh-rules-btn">
            <RefreshCw size={14} className="mr-1" />Re-scan
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="rules-summary">
          <SummaryCard label="Total Issues" count={summary.total_violations} icon={ShieldAlert} color="text-white" />
          <SummaryCard label="Critical" count={summary.critical} icon={AlertTriangle} color="text-red-400" />
          <SummaryCard label="Warnings" count={summary.warnings} icon={AlertTriangle} color="text-amber-400" />
          <SummaryCard label="Info" count={summary.info} icon={Info} color="text-blue-400" />
        </div>
      )}

      {/* Config Panel */}
      {showConfig && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="config-panel">
          <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-4">Rule Configuration</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-semibold mb-1.5 block">Max Stock Allocation %</label>
              <Input type="number" step="0.5" min="1" max="50"
                value={localConfig.max_stock_pct || 5}
                onChange={e => setLocalConfig(c => ({ ...c, max_stock_pct: parseFloat(e.target.value) || 5 }))}
                className="bg-[var(--bg-base)] border-white/10 text-sm w-32"
                data-testid="config-stock-pct" />
              <p className="text-[10px] text-[var(--text-muted)] mt-1">Flag stocks exceeding this % of total portfolio</p>
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-semibold mb-1.5 block">Max Sector Allocation %</label>
              <Input type="number" step="1" min="5" max="100"
                value={localConfig.max_sector_pct || 20}
                onChange={e => setLocalConfig(c => ({ ...c, max_sector_pct: parseFloat(e.target.value) || 20 }))}
                className="bg-[var(--bg-base)] border-white/10 text-sm w-32"
                data-testid="config-sector-pct" />
              <p className="text-[10px] text-[var(--text-muted)] mt-1">Flag sectors exceeding this % of total portfolio</p>
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-semibold mb-2 block">Enabled Rules</label>
            <div className="flex flex-wrap gap-3">
              {Object.entries(RULE_META).map(([key, meta]) => {
                const enabled = localConfig.rules_enabled?.[key] !== false;
                return (
                  <button key={key} onClick={() => setLocalConfig(c => ({
                    ...c, rules_enabled: { ...(c.rules_enabled || {}), [key]: !enabled }
                  }))}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs border transition-colors ${enabled ? "bg-white/5 border-white/20 text-white" : "bg-transparent border-white/5 text-[var(--text-muted)]"}`}
                    data-testid={`toggle-rule-${key}`}>
                    <meta.icon size={13} className={enabled ? meta.color : ""} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded text-xs" data-testid="save-config-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            Save Configuration
          </Button>
        </div>
      )}

      {/* Rule Filter Tabs */}
      <div className="flex gap-1.5 flex-wrap" data-testid="rule-filters">
        <FilterTab id="all" label="All" count={violations?.length || 0} active={filter} onClick={setFilter} />
        {Object.entries(RULE_META).map(([key, meta]) => (
          <FilterTab key={key} id={key} label={meta.label} count={ruleCounts[key] || 0} active={filter} onClick={setFilter} />
        ))}
      </div>

      {/* Violations List */}
      {filtered.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-10 flex flex-col items-center justify-center" data-testid="no-violations">
          <ShieldCheck size={40} className="text-[var(--positive)] mb-3" />
          <div className="text-sm text-[var(--text-secondary)] font-medium">All clear! No violations found.</div>
          <div className="text-xs text-[var(--text-muted)] mt-1">Your portfolio meets all configured rules.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="violations-list">
          {filtered.map((v, i) => {
            const style = SEVERITY_STYLE[v.severity] || SEVERITY_STYLE.info;
            const meta = RULE_META[v.rule] || { label: v.rule, icon: Info, color: "text-white" };
            const Icon = meta.icon;
            return (
              <div key={i} className={`${style.bg} border ${style.border} rounded-md p-4`} data-testid={`violation-${i}`}>
                <div className="flex items-start gap-3">
                  <Icon size={18} className={`${meta.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`text-sm font-medium ${style.text}`}>{v.message}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${style.badge} uppercase tracking-wider font-semibold`}>{v.severity}</span>
                    </div>
                    <div className="text-xs text-[var(--text-muted)]">{v.detail}</div>
                  </div>
                  {v.value > 0 && (
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono text-sm text-[var(--text-secondary)]">{fmtINR(v.value)}</div>
                      {v.pct && <div className="font-mono text-[10px] text-[var(--text-muted)]">{v.pct}%</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, count, icon: Icon, color }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-3" data-testid={`summary-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{label}</span>
        <Icon size={14} className={color} />
      </div>
      <div className={`font-mono text-xl font-medium ${color}`}>{count}</div>
    </div>
  );
}

function FilterTab({ id, label, count, active, onClick }) {
  const isActive = active === id;
  return (
    <button onClick={() => onClick(id)}
      className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${isActive ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--bg-surface)] text-[var(--text-secondary)] border-white/10 hover:text-white"}`}
      data-testid={`filter-${id}`}>
      {label}{count > 0 && <span className="ml-1.5 font-mono opacity-70">{count}</span>}
    </button>
  );
}

function fmtINR(v) {
  if (!v && v !== 0) return "--";
  const abs = Math.abs(v);
  if (abs >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
